
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SearchOverlayProps {
  isSearchOpen: boolean;
  searchQuery: string;
  searchResults: Array<{name: string, path: string, type?: string}>;
  searchInputRef: React.RefObject<HTMLInputElement>;
  toggleSearch: () => void;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchSubmit: (e: React.FormEvent) => void;
}

const SearchOverlay: React.FC<SearchOverlayProps> = ({
  isSearchOpen,
  searchQuery,
  searchResults,
  searchInputRef,
  toggleSearch,
  handleSearchChange,
  handleSearchSubmit
}) => {
  const navigate = useNavigate();

  const handleResultClick = (path: string) => {
    navigate(path);
    toggleSearch();
  };

  return (
    <div 
      className={cn(
        'fixed inset-0 bg-[#3E2723]/80 supports-backdrop-blur:backdrop-blur-md z-40 transition-opacity duration-300',
        isSearchOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}
      aria-hidden={!isSearchOpen}
    >
      <div className="container mx-auto px-4 pt-32">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-[#3E2723]">Search Cats About Town Tours</h2>
            <button 
              onClick={toggleSearch}
              className="text-[#3E2723] hover:text-[#657C56] transition-colors p-2 rounded-full"
              aria-label="Close search"
            >
              <X size={24} aria-hidden="true" />
            </button>
          </div>
          
          <form onSubmit={handleSearchSubmit} role="search">
            <div className="relative">
              <input 
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full border-2 border-[#8D6E63] rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[#657C56] focus:border-[#657C56] text-lg"
                placeholder="Search tours, blog posts, pages..."
                aria-label="Search website content"
                autoComplete="off"
              />
              <button 
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#8D6E63]"
                aria-label="Submit search"
              >
                <Search size={24} aria-hidden="true" />
              </button>
            </div>
          </form>
          
          {searchQuery.length > 1 && (
            <div className="mt-4">
              <div role="region" aria-label="Search results" className="max-h-60 overflow-y-auto">
                {searchResults.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {searchResults.map((result, index) => (
                      <li key={`${result.path}-${index}`}>
                        <button
                          className="w-full text-left py-3 px-2 hover:bg-[#FDF6ED] transition-colors rounded flex items-center justify-between"
                          onClick={() => handleResultClick(result.path)}
                        >
                          <span>{result.name}</span>
                          {result.type && (
                            <span className="text-xs px-2 py-1 bg-[#F5F5DC] text-[#657C56] rounded-full">
                              {result.type === 'tour' ? 'Tour' : 'Page'}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="py-3 text-center text-gray-500">No results found for "{searchQuery}"</p>
                )}
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-200 text-sm text-center text-gray-500">
                Press Enter to search or click on a result
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
